import CloseIcon from '@mui/icons-material/Close';
import MenuIcon from '@mui/icons-material/Menu';
import {
  Box,
  Button,
  SwipeableDrawer,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
} from '@mui/material';
import React, { useState } from 'react';

// Detect iOS to avoid edge swipe conflicts with drawer gestures.
const iOS =
  typeof navigator !== 'undefined' &&
  /iPad|iPhone|iPod/.test(navigator.userAgent);

/**
 * List item used as a child of `NavDrawer`.
 *
 * @param props - The component props.
 * @param props.icon - Optional leading icon shown before the label
 * @param props.label - Text shown in the list item.
 * @param props.onClick - Handler fired when the item is clicked.
 * @returns The rendered drawer list item.
 */
export function NavDrawerItem({
  icon,
  label,
  onClick,
}: {
  icon?: React.ReactNode;
  label: string;
  onClick: () => void;
}) {
  return (
    <ListItem key={label} disablePadding>
      <ListItemButton onClick={onClick} sx={{ paddingLeft: 0 }}>
        {icon && (
          <ListItemIcon sx={{ height: 24, width: 24 }}>{icon}</ListItemIcon>
        )}
        <ListItemText primary={label} />
      </ListItemButton>
    </ListItem>
  );
}

/*
 * Sidebar navigation component drawer.
 *
 * @returns The rendered toggle button and navigation drawer.
 */
export function NavDrawer({ children }: { children: React.ReactNode }) {
  const [open, setOpen] = useState(false);
  const toggleDrawer = (newOpen: boolean) => () => {
    setOpen(newOpen);
  };

  return (
    <>
      <Button
        onClick={toggleDrawer(true)}
        sx={{
          display: 'inline-block',
          minWidth: '0px',
          padding: 0,
          zIndex: 1200, // Ensure the button sits above the swipe area
        }}
      >
        <MenuIcon />
      </Button>
      <SwipeableDrawer
        anchor="right"
        // On iOS Safari, the edge swipe is used for back/forward navigation.
        // Setting these disabled props avoids clashing with swipe gestures.
        disableBackdropTransition={!iOS}
        disableDiscovery={iOS}
        disableSwipeToOpen={false}
        open={open}
        onClose={toggleDrawer(false)}
        onOpen={toggleDrawer(true)}
        minFlingVelocity={300}
        swipeAreaWidth={10}
        PaperProps={{
          sx: {
            overflow: 'visible',
            width: 300,
          },
        }}
      >
        <IconButton
          onClick={toggleDrawer(false)}
          sx={{
            '&:hover': { bgcolor: 'transparent' },
            color: 'white',
            left: -48,
            position: 'absolute',
            top: 10,
          }}
        >
          <CloseIcon />
        </IconButton>
        <Box
          sx={{ width: '100%' }}
          role="presentation"
          onClick={toggleDrawer(false)}
        >
          <List>
            {React.Children.map(children, (child) => {
              // Only accepts <NavDrawerItem> children
              if (
                !React.isValidElement(child) ||
                child.type !== NavDrawerItem
              ) {
                return null;
              }

              return child;
            })}
          </List>
        </Box>
      </SwipeableDrawer>
    </>
  );
}
